import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus } from "lucide-react";
import { Container, Chapter, FadeUp } from "./primitives";

export const FAQS = [
  {
    q: "How does lead discovery work?",
    a: "Our AI continuously scans public conversations across supported platforms, applies your ICP filters (industry, seniority, geography), scores intent in real time and surfaces the highest-value threads. You review, our AI suggests replies, you approve — done.",
  },
  {
    q: "Is browser automation safe?",
    a: "Yes. We use official OAuth APIs wherever available and secure, audited browser sessions only for supported actions. Every action is logged, rate-limited and reversible. Nothing scraped, nothing shady.",
  },
  {
    q: "Can I manage multiple accounts?",
    a: "Absolutely. Business and Enterprise plans support multi-account workspaces with granular permissions, so a single team can operate several social profiles safely under one roof.",
  },
  {
    q: "Does it support teams?",
    a: "Team-first from day one. Roles, review queues, approval chains, activity feeds, audit logs, SSO/SAML on Business and Enterprise. Everyone sees the same source of truth.",
  },
  {
    q: "Can I export leads?",
    a: "Anytime, no lock-in. CSV, JSON or direct sync to HubSpot, Salesforce, Pipedrive, Notion and Google Sheets on Professional and above.",
  },
  {
    q: "Can I use my own AI API?",
    a: "Yes — bring your own OpenAI, Anthropic or Gemini keys on Business+, and we’ll route all generation through your provider with your usage limits.",
  },
];

export default function FAQ() {
  const [open, setOpen] = useState(0);

  return (
    <section id="faq" data-testid="faq-section" className="relative py-32 md:py-40 border-t border-white/10">
      <Container>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5">
            <Chapter
              number="10"
              kicker="FAQ"
              title={<>Questions we get, <span className="font-serif-x italic">candidly</span> answered.</>}
              description="Still curious? Reach out — we answer everything in under 24h."
            />
          </div>

          <div className="lg:col-span-7">
            <div className="border-t border-white/10">
              {FAQS.map((f, i) => (
                <FadeUp key={f.q} delay={i * 0.03}>
                  <div className="border-b border-white/10">
                    <button
                      data-testid={`faq-toggle-${i}`}
                      onClick={() => setOpen(open === i ? -1 : i)}
                      className="w-full flex items-center justify-between gap-6 py-6 text-left group"
                    >
                      <span className="font-display text-lg md:text-xl tracking-[-0.02em] text-white">
                        {f.q}
                      </span>
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-white/15 bg-white/[0.03] group-hover:bg-white/[0.08] transition-colors">
                        {open === i ? <Minus className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
                      </span>
                    </button>
                    <AnimatePresence initial={false}>
                      {open === i && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                          className="overflow-hidden"
                        >
                          <div className="pb-6 pr-14 text-[15px] text-white/60 leading-relaxed">
                            {f.a}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </FadeUp>
              ))}
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
}
